import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useScrolled, useMediaQuery } from "../../hooks";
import { useTheme } from "../../context/ThemeContext";
import {
  ProteconsLogo,
  MenuIcon,
  CloseIcon,
} from "../../assets/icons/SocialIcons";
import SocialLinks from "../common/SocialLinks";
import ThemeToggle from "../common/ThemeToggle";
import FontSizeToggle from "../common/FontSizeToggle";
import "./Header.css";

// Primary links shown flat in the desktop nav
const PRIMARY_LINKS = [
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
];

// Grouped into "Products" dropdown
const PRODUCTS_LINKS = [
  {
    label: "AMS Suite",
    path: "/product-development",
    icon: "🏗️",
    desc: "AI-powered Asset Management",
  },
  {
    label: "Cloud Kitchen",
    path: "/cloud-kitchen",
    icon: "☁️",
    desc: "End-to-end Kitchen Ecosystem",
  },
  {
    label: "Smart City Tracking",
    path: "/tracking-smart-city",
    icon: "🏙️",
    desc: "IoT-Powered Urban Intelligence",
  },
  {
    label: "Collection Management",
    path: "/collection-management",
    icon: "📲",
    desc: "Smart Collection & Payment Workflows",
  },
];

// Grouped into "Company" dropdown
const COMPANY_LINKS = [
  {
    label: "Our Team",
    path: "/team",
    icon: "👥",
    desc: "Meet the ProtecoNINs",
  },
  {
    label: "Global Reach",
    path: "/global-reach",
    icon: "🌍",
    desc: "5 hubs · 5 continents",
  },
  {
    label: "Events",
    path: "/events",
    icon: "🎪",
    desc: "Conferences & culture",
  },
  {
    label: "Publications",
    path: "/publications",
    icon: "📰",
    desc: "Thought leadership & PoVs",
  },
  { label: "Careers", path: "/careers", icon: "💼", desc: "Join the team" },
];

// All links for the mobile grid
const MOBILE_LINKS = [
  { label: "About", path: "/about", icon: "🏢" },
  { label: "Services", path: "/services", icon: "⚙️" },
  { label: "AMS Suite", path: "/product-development", icon: "🏗️" },
  { label: "Cloud Kitchen", path: "/cloud-kitchen", icon: "☁️" },
  { label: "Smart City", path: "/tracking-smart-city", icon: "🏙️" },
  { label: "Collection Management", path: "/collection-management", icon: "📲" },
  { label: "Our Team", path: "/team", icon: "👥" },
  { label: "Global Reach", path: "/global-reach", icon: "🌍" },
  { label: "Events", path: "/events", icon: "🎪" },
  { label: "Publications", path: "/publications", icon: "📰" },
  { label: "Careers", path: "/careers", icon: "💼" },
  { label: "Contact", path: "/contact", icon: "✉️" },
];

export default function Header() {
  const scrolled = useScrolled(50);
  const isMobile = useMediaQuery("(max-width: 960px)");
  const { menuOpen, toggleMenu, closeMenu } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openDrop, setOpenDrop] = useState(null); // 'products' | 'company' | null

  const isActive = (path) =>
    pathname === path || (path !== "/" && pathname.startsWith(path));
  const isProductsActive = PRODUCTS_LINKS.some((l) => isActive(l.path));
  const isCompanyActive = COMPANY_LINKS.some((l) => isActive(l.path));

  const goTo = (path) => {
    closeMenu();
    navigate(path);
  };

  return (
    <>
      <nav className={`header ${scrolled ? "header--scrolled" : ""}`}>
        <div className="container header__inner">
          {/* ── Logo ── */}
          <Link to="/" className="header__logo" onClick={closeMenu}>
            <ProteconsLogo size={100} />
          </Link>

          {/* ── Desktop nav ── */}
          {!isMobile && (
            <nav className="header__nav" aria-label="Main navigation">
              {PRIMARY_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`header__link ${isActive(link.path) ? "header__link--active" : ""}`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Products dropdown */}
              <div
                className={`header__drop-wrap ${openDrop === "products" ? "header__drop-wrap--open" : ""}`}
                onMouseEnter={() => setOpenDrop("products")}
                onMouseLeave={() => setOpenDrop(null)}
              >
                <button
                  className={`header__link header__drop-trigger ${isProductsActive ? "header__link--active" : ""}`}
                  aria-expanded={openDrop === "products"}
                  aria-haspopup="true"
                >
                  Products
                  <svg
                    className="header__chevron"
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M1 1l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className="header__drop-panel" role="menu">
                  {PRODUCTS_LINKS.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      role="menuitem"
                      className={`header__drop-item ${isActive(link.path) ? "header__drop-item--active" : ""}`}
                      onClick={() => setOpenDrop(null)}
                    >
                      <span className="header__drop-icon">{link.icon}</span>
                      <span>
                        <span className="header__drop-label">{link.label}</span>
                        <span className="header__drop-desc">{link.desc}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Company dropdown */}
              <div
                className={`header__drop-wrap ${openDrop === "company" ? "header__drop-wrap--open" : ""}`}
                onMouseEnter={() => setOpenDrop("company")}
                onMouseLeave={() => setOpenDrop(null)}
              >
                <button
                  className={`header__link header__drop-trigger ${isCompanyActive ? "header__link--active" : ""}`}
                  aria-expanded={openDrop === "company"}
                  aria-haspopup="true"
                >
                  Company
                  <svg
                    className="header__chevron"
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M1 1l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className="header__drop-panel" role="menu">
                  {COMPANY_LINKS.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      role="menuitem"
                      className={`header__drop-item ${isActive(link.path) ? "header__drop-item--active" : ""}`}
                      onClick={() => setOpenDrop(null)}
                    >
                      <span className="header__drop-icon">{link.icon}</span>
                      <span>
                        <span className="header__drop-label">{link.label}</span>
                        <span className="header__drop-desc">{link.desc}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </nav>
          )}

          {/* ── Right controls ── */}
          <div className="header__actions">
            {!isMobile && <FontSizeToggle />}
            <ThemeToggle />
            {!isMobile && (
              <Link to="/contact" className="btn btn--primary header__cta">
                Get Consultation
              </Link>
            )}
            {isMobile && (
              <button
                className={`header__toggle ${menuOpen ? "header__toggle--open" : ""}`}
                onClick={toggleMenu}
                aria-label="Toggle navigation menu"
                aria-expanded={menuOpen}
              >
                {menuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* ── Mobile full-screen menu ── */}
      {menuOpen && isMobile && (
        <div
          className="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          {/* Menu header */}
          <div className="mobile-menu__header">
            <Link to="/" className="header__logo" onClick={closeMenu}>
              <ProteconsLogo size={76} />
            </Link>
            <button
              className="mobile-menu__close"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <CloseIcon />
            </button>
          </div>

          {/* 2-column link grid */}
          <div className="mobile-menu__grid">
            {MOBILE_LINKS.map((link) => (
              <button
                key={link.path}
                className={`mobile-menu__item ${isActive(link.path) ? "mobile-menu__item--active" : ""}`}
                onClick={() => goTo(link.path)}
              >
                <span className="mobile-menu__item-icon">{link.icon}</span>
                <span className="mobile-menu__item-label">{link.label}</span>
              </button>
            ))}
          </div>

          {/* Footer: theme + CTA + socials */}
          <div className="mobile-menu__footer">
            <FontSizeToggle />
            <ThemeToggle />
            <Link
              to="/contact"
              className="btn btn--primary mobile-menu__cta"
              onClick={closeMenu}
            >
              Get Started
            </Link>
            <SocialLinks size={40} />
          </div>
        </div>
      )}
    </>
  );
}
